import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Box, Tab, Tabs, Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  DigitalCase,
  DigitalQuestion,
  DigitalSection,
  DigitalSubmission,
  DigitalSubmissionMeta,
  Enrollment,
  FullSubmissionMap,
  QuestionCaseSetting,
} from '../../../../redux/types';
import AnswerSummary from '../../../digitalTrial/AnswerSummary';
import ContextQuestionInputForm from '../ContextQuestionInputForm';
import QuestionInputFormPreview from '../QuestionInputFormPreview';
import usePendingCompulsoryQuestions from '../../../../hooks/survey/usePendingCompulsoryQuestions';
import usePendingCompulsoryCtxQuestions from '../../../../hooks/survey/usePendingCompulsoryCtxQuestions';

type Props = {
  trialId: number;
  enrollment?: Enrollment;
  userRole?: string;
  showSubmissions?: boolean;
  isPublicView: boolean;
  displayReview: boolean;
  selectedSection?: DigitalSection;
  sections: DigitalSection[];
  cases: DigitalCase[];
  questions: DigitalQuestion[];
  contextQuestions: DigitalQuestion[];
  questionCaseSettings: QuestionCaseSetting[];
  settingsDictionary: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  questionCaseSubmissionMap: Record<number, Record<number, DigitalSubmission> | undefined>;
  contextSubmissionMapByQuestionId: Record<number, DigitalSubmission | undefined>;
  fullSubmissionMap: FullSubmissionMap;
  fullCtxSubmissionMap: Record<number, Record<number, DigitalSubmission>>;
  dsms?: DigitalSubmissionMeta[];
  onSectionClick: (event: React.SyntheticEvent | null, newValue: number) => void;
  onCaseClick: ((questionId: number, caseId: number) => void) | undefined;
  onNavigateCtxQuestion: (qId: number) => void;
  onHighlightOptout: () => void;
};

const SubmissionReviewDialog: React.FC<Props> = ({
  trialId,
  enrollment,
  userRole,
  showSubmissions,
  isPublicView,
  displayReview,
  selectedSection,
  sections,
  cases,
  questions,
  contextQuestions,
  questionCaseSettings: settings,
  settingsDictionary,
  questionCaseSubmissionMap,
  contextSubmissionMapByQuestionId,
  fullSubmissionMap,
  fullCtxSubmissionMap,
  dsms,
  onCaseClick,
  onSectionClick,
  onNavigateCtxQuestion,
  onHighlightOptout,
}: Props) => {
  const [incompleteSettingMap, pendingSecId] = usePendingCompulsoryQuestions(
    settings, fullSubmissionMap, dsms,
  );
  const [incompleteCtxSettingMap, pendingCtxSecId] = usePendingCompulsoryCtxQuestions(
    settings, fullCtxSubmissionMap, dsms,
  );
  const [selectedDsm, setSelectedDsm] = useState<DigitalSubmissionMeta | undefined>(undefined);

  useEffect(() => {
    const pendingSectionId = pendingSecId !== undefined ? pendingSecId : pendingCtxSecId;
    if (!pendingSectionId) {
      return;
    }
    onSectionClick(null, pendingSectionId);
  }, [onSectionClick, pendingSecId, pendingCtxSecId]);

  useEffect(() => {
    if (selectedSection) {
      setSelectedDsm(dsms?.find((dsm) => dsm.selectedSectionId === selectedSection.id));
    }
  }, [dsms, selectedSection]);

  const addOrUpdateDigitalSubmission = useCallback(() => (null), []);
  return (
    <Box>
      {
        displayReview && (
          <>
            <Box sx={{ p: 3, minHeight: '75vh' }} className="hideprint">
              <Box display="flex" alignItems="center" gap={2}>
                <Tabs
                  sx={{ flexGrow: 1 }}
                  value={selectedSection?.id}
                  onChange={onSectionClick}
                  variant="scrollable"
                  scrollButtons
                >
                  {
                    sections.map((s) => <Tab label={s.name} key={s.id} value={s.id} />)
                  }
                </Tabs>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  display: 'flex', justifyContent: 'space-between', m: 2, p: '0.5rem',
                }}
              >
                <FormattedMessage id="answerSummary.title" />
                {
                  selectedSection && selectedSection.canOptOut && (
                    <Box
                      onClick={onHighlightOptout}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        cursor: 'pointer',
                        p: '0.25rem',
                        '&:hover': {
                          backgroundColor: 'ButtonFace',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        },
                      }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        <FormattedMessage id="survey.labelOptout" />
                        &nbsp;
                      </Typography>
                      {
                        selectedDsm?.isOptOut ? (
                          <Typography variant="body1" sx={{ color: 'green', fontWeight: 'bold' }}>
                            <FormattedMessage id="yes" />
                          </Typography>
                        )
                          : (
                            <Typography variant="body1" sx={{ color: 'red', fontWeight: 'bold' }}>
                              <FormattedMessage id="no" />
                            </Typography>
                          )
                      }
                    </Box>
                  )
                }
              </Typography>
              <AnswerSummary
                digitalCases={cases}
                digitalQuestions={questions}
                submissionsMapByQuestionAndCaseId={questionCaseSubmissionMap}
                compulsorySettingsMapByQuestionAndCaseId={incompleteSettingMap}
                settingsDictionary={settingsDictionary}
                onCaseClick={onCaseClick}
              />
              <Typography variant="h6" sx={{ mt: 5, ml: 2 }}>
                <FormattedMessage id="review.methodicalQuestionTitle" />
              </Typography>
              {
                selectedSection
                  && enrollment
                  && (userRole !== 'admin' || showSubmissions)
                  && !isPublicView ? (
                    <ContextQuestionInputForm
                      trialId={Number(trialId)}
                      sectionId={selectedSection.id}
                      enrollmentId={enrollment.id}
                      questions={contextQuestions}
                      contextSubmissionByQuestionId={contextSubmissionMapByQuestionId}
                      addOrUpdateDigitalSubmission={addOrUpdateDigitalSubmission}
                      navigateToQuestion={onNavigateCtxQuestion}
                      compulsoryCtxSettingMap={incompleteCtxSettingMap}
                      disabled
                    />
                  ) : (
                    <QuestionInputFormPreview
                      questions={contextQuestions}
                      settingsDictionary={settingsDictionary}
                      compulsoryCtxSettingMap={incompleteCtxSettingMap}
                    />
                  )
              }
            </Box>
          </>
        )
      }
    </Box>
  );
};

export default SubmissionReviewDialog;
